/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from "@mui/material";
import BaseButton from "components/common/Button/Base";
import AlertDialog from "components/common/Dialog/Alert";
import { StoreType, SubType } from "models/business.model";
import { updateCustomPrompt } from "pages/remotes";
import { PALETTE } from "styles/base/variables";

const DEFAULT_SAVE_ERROR = "저장 중 오류가 발생했습니다.";

function getErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data;
  const msg = data?.message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return fallback;
}

function getPlaceholder(storeType: StoreType, subType?: SubType): string {
  if (storeType === "bank")
    return "예) '네이버페이' 입금 건은 외상매출금으로 처리해주세요.";
  if (storeType === "tax" && subType === "SALES")
    return "예) 컨설팅 용역 매출은 수수료수익으로 처리해주세요.";
  if (storeType === "tax")
    return "예) 임차료 항목은 지급임차료로 처리해주세요.";
  return "예) PG사 결제건은 항상 소모품비로 처리해주세요."; // card
}

interface CustomPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  storeType: StoreType;
  subType?: SubType;
  businessId: string;
  // 팝업이 열리기 전에 미리 조회해둔 값. 팝업을 여는 시점에는 이미 조회가
  // 끝나 있어야 placeholder가 잠깐 보였다 사라지는 깜빡임이 없다.
  initialContent: string;
  initialLoadError: string | null;
}

function CustomPromptDialog({
  isOpen,
  onClose,
  storeType,
  subType,
  businessId,
  initialContent,
  initialLoadError
}: CustomPromptDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [content, setContent] = useState("");

  const [isSaveSuccessAlertOpen, setIsSaveSuccessAlertOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setContent(initialContent);
    setErrorMessage(initialLoadError);
  }, [isOpen, initialContent, initialLoadError]);

  const resetDialogData = useCallback(() => {
    setContent("");
    setErrorMessage(null);
    setIsSaving(false);
  }, []);

  const handleSave = () => {
    if (!businessId || isSaving) return;

    setIsSaving(true);
    setErrorMessage(null);
    updateCustomPrompt(storeType, businessId, content, subType)
      .then(() => {
        setIsSaving(false);
        onClose();
        setIsSaveSuccessAlertOpen(true);
      })
      .catch((err: unknown) => {
        setIsSaving(false);
        setErrorMessage(getErrorMessage(err, DEFAULT_SAVE_ERROR));
      });
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionProps={{ onExited: resetDialogData }}
      >
        <DialogTitle>AI 회계처리 설정</DialogTitle>
        <DialogContent>
          <Typography color={PALETTE.textMid} fontSize={13} marginBottom="12px">
            AI 회계처리에 적용하고 싶은 지침을 적어주세요.
          </Typography>
          {errorMessage && (
            <Typography color={PALETTE.error} fontSize={13} marginBottom="8px">
              {errorMessage}
            </Typography>
          )}
          <textarea
            value={content}
            disabled={isSaving}
            onChange={(e) => setContent(e.target.value)}
            placeholder={getPlaceholder(storeType, subType)}
            css={css`
              width: 100%;
              min-height: 200px;
              padding: 12px;
              font-size: 14px;
              font-family: inherit;
              line-height: 1.5;
              resize: vertical;
              box-sizing: border-box;
              border: 1px solid ${PALETTE.border};
              border-radius: 4px;
              background-color: ${isSaving
                ? PALETTE.backgroundDisabled
                : PALETTE.light};

              &:focus {
                outline: none;
                border-color: ${PALETTE.primary};
              }
            `}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 20px 0" }}>
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={onClose}
            disabled={isSaving}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "저장 중" : "저장"}
          </BaseButton>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isSaveSuccessAlertOpen}
        setIsOpen={setIsSaveSuccessAlertOpen}
        alertData={{ type: "success", message: "저장되었습니다." }}
      />
    </>
  );
}

export default CustomPromptDialog;
